/**
 * AdminCollectionDetailPage
 *
 * Displays comprehensive collection analytics including:
 * - Collection metadata and stats
 * - NFT count breakdowns by status
 * - Rarity statistics
 * - Job history (indexing, offers, history)
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCollectionDetail } from '../services/adminApi';
import { AdminLayout } from '../components/AdminLayout';
import { CollectionStatsPanel } from '../components/admin/CollectionStatsPanel';
import { JobStatusTable } from '../components/admin/JobStatusTable';
import { JobTriggerButtons } from '../components/admin/JobTriggerButtons';
import { ErrorDisplay } from '../components/admin/ErrorDisplay';
import type { CollectionDetailResponse } from '../types/admin';

export function AdminCollectionDetailPage() {
  const { issuer, taxon } = useParams<{ issuer: string; taxon: string }>();
  const [collectionDetail, setCollectionDetail] = useState<CollectionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCollectionDetail = async () => {
    if (!issuer || !taxon) {
      setError('Missing issuer or taxon in URL');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Parse taxon: 'all' means null (issuer-wide), otherwise parse as integer
      const parsedTaxon = taxon === 'all' ? null : parseInt(taxon, 10);
      const data = await getCollectionDetail(issuer, parsedTaxon);
      setCollectionDetail(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load collection details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollectionDetail();
  }, [issuer, taxon]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading collection details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto">
          <ErrorDisplay error={error} onRetry={loadCollectionDetail} />
        </div>
      </AdminLayout>
    );
  }

  if (!collectionDetail) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-gray-600">
            Collection not found
          </div>
        </div>
      </AdminLayout>
    );
  }

  const { collection, nftStats, rarityStats, traitStats, floorPriceHistoryStats, jobs } = collectionDetail;
  const isWhitelistedOnly = collection === null;

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
      {/* Header with Refresh Button */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Collection Details</h1>
          <p className="text-gray-600 mt-1">
            {issuer} / {taxon === 'all' ? <span className="text-purple-600 font-medium">Issuer-Wide</span> : `Taxon ${taxon}`}
          </p>
        </div>
        <button
          onClick={loadCollectionDetail}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Whitelisted but Not Indexed Notice */}
      {isWhitelistedOnly && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="h-6 w-6 text-yellow-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800 mb-1">Collection Not Yet Indexed</h3>
              <p className="text-sm text-yellow-700">
                This issuer/taxon pair is whitelisted but hasn't been indexed yet.
                Use the "Trigger Collection Indexing" button below to start indexing NFTs from the XRPL.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Collection Stats Panel */}
      {!isWhitelistedOnly && (
        <div className="mb-6">
          <CollectionStatsPanel
            collection={collection!}
            nftStats={nftStats}
            onMethodologyUpdated={loadCollectionDetail}
          />
        </div>
      )}

      {/* Floor Price History Stats */}
      {!isWhitelistedOnly && floorPriceHistoryStats && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Floor Price History</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600">Total Days</div>
              <div className="text-2xl font-semibold text-gray-900">
                {floorPriceHistoryStats.totalEntries.toLocaleString()}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600">Date Range</div>
              <div className="text-sm font-medium text-gray-900">
                {floorPriceHistoryStats.dateRange.oldest && floorPriceHistoryStats.dateRange.newest ? (
                  <>
                    {floorPriceHistoryStats.dateRange.oldest}
                    <br />
                    to {floorPriceHistoryStats.dateRange.newest}
                  </>
                ) : (
                  'N/A'
                )}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600">From Offers</div>
              <div className="text-2xl font-semibold text-blue-600">
                {floorPriceHistoryStats.sourceBreakdown.offer.toLocaleString()}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600">From Sales</div>
              <div className="text-2xl font-semibold text-green-600">
                {floorPriceHistoryStats.sourceBreakdown.sale.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Floor Price History Notice */}
      {!isWhitelistedOnly && !floorPriceHistoryStats && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="text-sm text-gray-600">
            No floor price history data available. Run a history fetch job to backfill from sales data.
          </div>
        </div>
      )}

      {/* Job Trigger Buttons */}
      <div className="mb-6">
        <JobTriggerButtons
          issuer={issuer!}
          taxon={taxon === 'all' ? null : parseInt(taxon!, 10)}
          jobs={jobs}
          collection={collection}
          onJobTriggered={loadCollectionDetail}
        />
      </div>

      {/* Job Status Tables */}
      <div className="space-y-6">
        {/* Collection Indexing Jobs */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Collection Indexing Jobs ({jobs.indexing.length})
          </h3>
          <JobStatusTable
            jobs={jobs.indexing}
            jobType="indexing"
            emptyMessage="No indexing jobs found"
          />
        </div>

        {/* Offer Sync Jobs */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Offer Sync Jobs ({jobs.offerSync.length})
          </h3>
          <JobStatusTable
            jobs={jobs.offerSync}
            jobType="offerSync"
            emptyMessage="No offer sync jobs found"
          />
        </div>

        {/* Collection History Jobs */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Collection History Jobs ({jobs.history.length})
          </h3>
          <JobStatusTable
            jobs={jobs.history}
            jobType="history"
            emptyMessage="No history jobs found"
          />
        </div>
      </div>

      {/* Trait Breakdown Card */}
      {traitStats && Object.keys(traitStats).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trait Breakdown</h3>
          <div className="space-y-6">
            {Object.entries(traitStats)
              .sort((a, b) => a[0].localeCompare(b[0])) // Sort trait types alphabetically
              .map(([traitType, values]: [string, Record<string, { count: number; percentage: number }>]) => (
                <div key={traitType}>
                  <h4 className="font-medium text-gray-900 mb-3">{traitType}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(values)
                      .sort((a, b) => b[1].count - a[1].count) // Sort by count descending
                      .map(([value, stats]: [string, { count: number; percentage: number }]) => (
                        <div key={value} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <div className="text-sm font-medium text-gray-900 truncate" title={value}>
                            {value}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {stats.count} ({stats.percentage.toFixed(2)}%)
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
